import React, { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    indexActivitiesByTeacherType,
    DestroyActivity,
    clssInfo,
    createActivity,
    updateAvaliacao,
    GetMatter,
    getIstQuarter,
    getIIndQuarter,
    getIIIrdQuarter,
    getIVthQuarter,
    getVthQuarter,
    getVIthQuarter
} from '../../Api'

import {
    Container,
    ContainerDivs,
    Header,
    Title,
    NewActivityButton,
    Toolbar,
    ToolbarActions,
    SearchInput,
    ToggleAllButton,
    EmptyState,
    MatterCard,
    MatterHeader,
    MatterTitle,
    Badge,
    ChevronWrap,
    MatterBody,
    BimesterCard,
    BimesterHeader,
    BimesterTitle,
    BimesterBody,
    ActivityRow,
    ActivityInfo,
    ActivityTitle,
    ActivityMeta,
    Tag,
    ActivityActions,
    ViewGradesButton,
    EditButton,
    DeleteButton,
    Overlay,
    Modal,
    ModalTitle,
    ModalText,
    ModalActions,
    ConfirmDelete,
    Cancel,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    FormCard,
    FormHeader,
    FormTitle,
    FormSubtitle,
    FormGroup,
    FormLabel,
    FormInput,
    FormSelect,
    FormError,
    FormActions,
    SaveButton,
    CancelButton,
    SkeletonCard,
    SkeletonLine
} from './style'
import SeloIAProf from '../../components/SeloIAProf';

// label → chave do bimestre no schema da Activity
const BIMESTRE_MAPPING = {
    "1º BIMESTRE": "id_iStQuarter",
    "2º BIMESTRE": "id_iiNdQuarter",
    "3º BIMESTRE": "id_iiiRdQuarter",
    "4º BIMESTRE": "id_ivThQuarter",
    "5º BIMESTRE": "id_vThQuarter",
    "6º BIMESTRE": "id_viThQuarter"
}

// ── helpers fora do componente (não recriam a cada render) ──────────
const extractId = (v) => {
    if (!v) return null
    if (typeof v === 'string') {
        const t = v.trim()
        if ((t.startsWith('"') && t.endsWith('"')) ||
            (t.startsWith('[') && t.endsWith(']')) ||
            (t.startsWith('{') && t.endsWith('}'))) {
            try { v = JSON.parse(t) } catch { /* mantém v */ }
        }
    }
    if (Array.isArray(v)) v = v[0]
    if (v && typeof v === 'object') v = v._id ?? v.id ?? null
    const s = String(v ?? '').trim()
    return /^[a-fA-F0-9]{24}$/.test(s) ? s : null
}

const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

const toNumber = (v) => {
    if (v === null || v === undefined || v === '') return 0
    const n = Number(String(v).replace(',', '.'))
    return Number.isFinite(n) ? n : 0
}

const fmt = (n) => {
    const v = Number(n) || 0
    return v.toFixed(2).replace('.', ',')
}

const bimOrder = (b) => parseInt(String(b).match(/\d+/)?.[0] || '99', 10)

/* ── Componente da linha de atividade (memoizado) ─────────────────
   Evita re-render de todas as linhas quando o usuário digita na busca
   ou abre/fecha outro bimestre.                                     */
const ActivityRowItem = memo(function ActivityRowItem({
    activity,
    isActivityOpen,
    studentTotal,
    onViewGrades,
    onEdit,
    onDelete
}) {
    const gradesCount = Array.isArray(activity.studentGrades)
        ? activity.studentGrades.length
        : 0

    const viewState = !isActivityOpen
        ? 'closed'
        : (gradesCount >= studentTotal ? 'done' : 'pending')

    const viewLabel = !isActivityOpen
        ? 'Período Fechado'
        : (gradesCount >= studentTotal ? 'Ver Notas' : 'Notas Pendentes')

    return (
        <ActivityRow>
            <ActivityInfo>
                <ActivityTitle>{activity.descricao}{activity?.origem === 'iaprof' && <SeloIAProf />}</ActivityTitle>
                <ActivityMeta>
                    {activity.tipo && <Tag $variant="tipo">{activity.tipo}</Tag>}
                    {activity.valor !== undefined && (
                        <Tag $variant="valor">Valor: {fmt(activity.valor)}</Tag>
                    )}
                    {Array.isArray(activity.studentGrades) && (
                        <Tag>{gradesCount} notas</Tag>
                    )}
                </ActivityMeta>
            </ActivityInfo>

            <ActivityActions>
                <ViewGradesButton
                    $state={viewState}
                    disabled={!isActivityOpen}
                    onClick={() => isActivityOpen && onViewGrades(activity)}
                    aria-label={`${viewLabel} — ${activity.descricao}`}
                >
                    {viewLabel}
                </ViewGradesButton>

                <EditButton
                    disabled={!isActivityOpen}
                    onClick={() => isActivityOpen && onEdit(activity)}
                    aria-label={`Editar ${activity.descricao}`}
                >
                    Editar
                </EditButton>

                <DeleteButton
                    disabled={!isActivityOpen}
                    onClick={() => isActivityOpen && onDelete(activity)}
                    aria-label={`Apagar ${activity.descricao}`}
                >
                    Apagar
                </DeleteButton>
            </ActivityActions>
        </ActivityRow>
    )
})

const Activities = () => {
    const navigate = useNavigate()

    // ── listagem ─────────────────────────────────────────────────────
    const [activities, setActivities] = useState([])
    const [loading, setLoading] = useState(true)             // dados principais
    const [loadingSecondary, setLoadingSecondary] = useState(true) // bimestres/matérias
    const [busca, setBusca] = useState('')
    const [debouncedBusca, setDebouncedBusca] = useState('')
    const [openMatters, setOpenMatters] = useState({})
    const [openBimesters, setOpenBimesters] = useState({})

    // ── delete ───────────────────────────────────────────────────────
    const [pendingDelete, setPendingDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)

    // ── contexto da turma/professor ──────────────────────────────────
    const [matters, setMatters] = useState([])
    const [year, setYear] = useState('')
    const [idClass, setIdClass] = useState('')
    const [idTeacher, setIdTeacher] = useState('')
    const [idTeacher02, setIdTeacher02] = useState(null)
    const [isPhysicalTeacher, setIsPhysicalTeacher] = useState(false)
    const [dailyStatus, setDailyStatus] = useState({})
    const [bimesterById, setBimesterById] = useState({})
    const [assessmentRegime, setAssessmentRegime] = useState('')
    const [contStudent, setContStudent] = useState([])

    const periodLabel = assessmentRegime === 'TRIMESTRAL' ? 'Trimestre' : 'Bimestre'
    const periodLabelLower = assessmentRegime === 'TRIMESTRAL' ? 'trimestre' : 'bimestre'

    // ── modal criar/editar ───────────────────────────────────────────
    const [showForm, setShowForm] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [form, setForm] = useState({
        bimonthly: '',
        id_matter: '',
        descricao: '',
        tipo: '',
        valor: ''
    })

    // ── debounce da busca ────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300)
        return () => clearTimeout(t)
    }, [busca])

    // ── carga em DUAS FASES (carregamento progressivo) ───────────────
    // Fase 1: turma + atividades  → libera a UI o quanto antes
    // Fase 2: matérias + bimestres → carregam em background
    useEffect(() => {
        let cancelled = false

            ; (async () => {
                setLoading(true)
                setLoadingSecondary(true)

                const id_class = sessionStorage.getItem('class-info')
                const id_emp = localStorage.getItem('Id_employee')
                const idSchool = safeParse(sessionStorage.getItem('id-school'))
                const physicalRaw = sessionStorage.getItem('physicalEducationTeacher')
                const regent02Raw = sessionStorage.getItem('classRegentTeacher02')

                sessionStorage.removeItem('id-activity')

                const myId = extractId(id_emp)
                const physicalId = extractId(safeParse(physicalRaw))
                const regent02Id = extractId(safeParse(regent02Raw))

                const amIPhysical = !!physicalId && myId === physicalId
                const teacher_type = amIPhysical ? 'ED_FISICA' : 'REGENTE'

                try {
                    // ── FASE 1 ─────────────────────────────────────────
                    const resClass = await clssInfo(id_class)
                    if (cancelled) return

                    const turma = resClass?.data?.data?.[0]
                    const yearValue = turma?.year
                    const students = turma?.id_student || []

                    sessionStorage.setItem('yearGrade', yearValue)

                    // dispara atividades já com o year correto
                    const actPromise = indexActivitiesByTeacherType({
                        id_class,
                        year: yearValue,
                        teacher_type
                    })

                    // dispara dados secundários em PARALELO (mas sem bloquear)
                    const secondaryPromise = Promise.all([
                        GetMatter(idSchool),
                        getIstQuarter(yearValue, idSchool),
                        getIIndQuarter(yearValue, idSchool),
                        getIIIrdQuarter(yearValue, idSchool),
                        getIVthQuarter(yearValue, idSchool),
                        getVthQuarter(yearValue, idSchool),
                        getVIthQuarter(yearValue, idSchool)
                    ])

                    // espera só pelas atividades (dado mais importante)
                    const resAct = await actPromise
                    if (cancelled) return

                    const acts = resAct?.data?.data || []

                    // libera a UI principal
                    setActivities(acts)
                    setContStudent(students)
                    setDailyStatus(turma?.dailyStatus || {})
                    setIsPhysicalTeacher(amIPhysical)
                    setYear(yearValue)
                    setIdClass(id_class)
                    setIdTeacher(myId)
                    setIdTeacher02(regent02Id)

                    // todos os bimestres iniciam fechados (recolhidos)

                    setLoading(false)

                    // ── FASE 2 ─────────────────────────────────────────
                    const [resMatter, q1, q2, q3, q4, q5, q6] = await secondaryPromise
                    if (cancelled) return

                    const allMatters = resMatter?.data?.data || []
                    const filteredMatters = allMatters.filter(m =>
                        amIPhysical ? m.name === 'EDUCAÇÃO FÍSICA' : m.name !== 'EDUCAÇÃO FÍSICA'
                    )

                    const pickFirst = (q) => q?.data?.data?.find(r => r) || null
                    const i = pickFirst(q1)
                    const ii = pickFirst(q2)
                    const iii = pickFirst(q3)
                    const iv = pickFirst(q4)
                    const v = pickFirst(q5)
                    const vi = pickFirst(q6)

                    const map = {}
                    if (i) map['1º BIMESTRE'] = i
                    if (ii) map['2º BIMESTRE'] = ii
                    if (iii) map['3º BIMESTRE'] = iii
                    if (iv) map['4º BIMESTRE'] = iv
                    if (v) map['5º BIMESTRE'] = v
                    if (vi) map['6º BIMESTRE'] = vi

                    setMatters(filteredMatters)
                    setBimesterById(map)
                    setAssessmentRegime(i?.assessmentRegime || '')
                    setLoadingSecondary(false)
                } catch (err) {
                    console.error('Erro ao carregar página:', err)
                    if (!cancelled) {
                        setLoading(false)
                        setLoadingSecondary(false)
                    }
                }
            })()

        return () => { cancelled = true }
    }, [])

    // ── scroll lock + ESC quando algum modal está aberto ─────────────
    useEffect(() => {
        const anyOpen = showForm || !!pendingDelete
        if (!anyOpen) return

        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKey = (e) => {
            if (e.key !== 'Escape') return
            if (pendingDelete && !deleting) setPendingDelete(null)
            else if (showForm && !submitting) {
                setShowForm(false)
                setEditingId(null)
                setErrorMessage('')
            }
        }
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKey)
        }
    }, [showForm, pendingDelete, submitting, deleting])

    // ── bimestres disponíveis no form ────────────────────────────────
    const availableBimesters = useMemo(() => {
        const base = assessmentRegime === 'TRIMESTRAL'
            ? ['1º BIMESTRE', '2º BIMESTRE', '3º BIMESTRE']
            : ['1º BIMESTRE', '2º BIMESTRE', '3º BIMESTRE', '4º BIMESTRE']
        return base.filter(b => bimesterById[b])
    }, [assessmentRegime, bimesterById])

    // ── agrupamento ─────────────────────────────────────────────────
    const grouped = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const groups = {}

        for (const a of activities) {
            if (q) {
                const haystack = [a.descricao, a.tipo, a.bimonthly, a.id_matter?.name]
                    .filter(Boolean).join(' ').toLowerCase()
                if (!haystack.includes(q)) continue
            }

            const bim = a.bimonthly || 'SEM BIMESTRE'
            const matterId = a.id_matter?._id || 'sem_materia'
            const matterName = a.id_matter?.name || 'Sem matéria'

            if (!groups[bim]) {
                groups[bim] = { bimester: bim, matters: {}, total: 0, somaBim: 0 }
            }
            if (!groups[bim].matters[matterId]) {
                groups[bim].matters[matterId] = {
                    id: matterId, name: matterName, activities: [], total: 0, soma: 0
                }
            }

            groups[bim].matters[matterId].activities.push(a)
            groups[bim].matters[matterId].total++
            groups[bim].matters[matterId].soma += toNumber(a.valor)
            groups[bim].total++
            groups[bim].somaBim += toNumber(a.valor)
        }

        return Object.values(groups)
            .sort((a, b) => bimOrder(a.bimester) - bimOrder(b.bimester))
            .map(b => ({
                ...b,
                mattersList: Object.values(b.matters)
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
            }))
    }, [activities, debouncedBusca])

    const notaDistribuida = useMemo(() => {
        if (!form.id_matter || !form.bimonthly) return 0
        return activities
            .filter(a =>
                a.bimonthly === form.bimonthly &&
                (a.id_matter?._id || a.id_matter) === form.id_matter &&
                a._id !== editingId
            )
            .reduce((acc, a) => acc + toNumber(a.valor), 0)
    }, [activities, form.id_matter, form.bimonthly, editingId])

    const isBimesterOpen = useMemo(() => {
        const st = dailyStatus?.[form.bimonthly]
        if (!st) return true
        const field = isPhysicalTeacher ? st.physicalEducationTeacher : st.regentTeacher
        return field !== 'fechado'
    }, [dailyStatus, form.bimonthly, isPhysicalTeacher])

    const currentBimesterMeta = bimesterById[form.bimonthly] || null
    const totalGradeBim = toNumber(currentBimesterMeta?.totalGrade)
    const averageGradeBim = toNumber(currentBimesterMeta?.averageGrade)

    const totalAtividades = useMemo(
        () => grouped.reduce((acc, m) => acc + m.total, 0),
        [grouped]
    )

    // ── toggles ──────────────────────────────────────────────────────
    const toggleMatter = useCallback((id) =>
        setOpenMatters(prev => ({ ...prev, [id]: !prev[id] })), [])

    const toggleBimester = useCallback((key) =>
        setOpenBimesters(prev => ({ ...prev, [key]: !prev[key] })), [])

    const allExpanded = useMemo(() => {
        if (grouped.length === 0) return false
        return grouped.every(b => {
            if (!openBimesters[b.bimester]) return false
            return b.mattersList.every(m =>
                openMatters[`${b.bimester}-${m.id}`]
            )
        })
    }, [grouped, openBimesters, openMatters])

    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setOpenBimesters({})
            setOpenMatters({})
        } else {
            const bims = {}, mts = {}
            for (const b of grouped) {
                bims[b.bimester] = true
                for (const m of b.mattersList) {
                    mts[`${b.bimester}-${m.id}`] = true
                }
            }
            setOpenBimesters(bims)
            setOpenMatters(mts)
        }
    }, [allExpanded, grouped])

    // ── ações da atividade (estáveis para memo) ─────────────────────
    const goGrades = useCallback((activity) => {
        console.log('idmater', activity)
        sessionStorage.setItem('id-activity', activity._id)
        sessionStorage.setItem('Selectmatter', activity.id_matter._id)
        navigate('/$num-quarter-grade')
    }, [navigate])

    const openEdit = useCallback((activity) => {
        setEditingId(activity._id)
        setErrorMessage('')
        setForm({
            bimonthly: activity.bimonthly || '1º BIMESTRE',
            id_matter: activity.id_matter?._id || activity.id_matter || '',
            descricao: activity.descricao || '',
            tipo: activity.tipo || 'Normal',
            valor: activity.valor !== null && activity.valor !== undefined
                ? String(activity.valor) : ''
        })
        setShowForm(true)
    }, [])

    const askDelete = useCallback((activity) => setPendingDelete(activity), [])

    // ── form ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditingId(null)
        setErrorMessage('')
        setForm({
            bimonthly: '', id_matter: '', descricao: '', tipo: '', valor: ''
        })
        setShowForm(true)
    }

    const closeForm = () => {
        if (submitting) return
        setShowForm(false)
        setEditingId(null)
        setErrorMessage('')
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        setErrorMessage('')
    }

    const handleSubmit = async (e) => {
        if (e?.preventDefault) e.preventDefault()
        if (submitting) return

        if (!form.descricao.trim()) {
            setErrorMessage('Informe a descrição da atividade.')
            return
        }
        if (!form.id_matter) {
            setErrorMessage('Selecione uma matéria.')
            return
        }
        if (!form.bimonthly) {
            setErrorMessage('Selecione um periodo.')
            return
        }
        const valorNum = toNumber(form.valor)
        if (valorNum <= 0) {
            setErrorMessage('Informe um valor válido (maior que zero).')
            return
        }

        if (!isBimesterOpen) {
            setErrorMessage('O diário deste periodo está fechado. Contate o Supervisor.')
            return
        }

        const bimDoc = bimesterById[form.bimonthly]
        if (!bimDoc?._id) {
            setErrorMessage('Periodo não está configurado para esta escola/ano.')
            return
        }
        const quarterIdKey = BIMESTRE_MAPPING[form.bimonthly]
        if (!quarterIdKey) {
            setErrorMessage('Periodo inválido.')
            return
        }

        if (totalGradeBim > 0) {
            const totalDistribuido = notaDistribuida + valorNum
            if (totalDistribuido > totalGradeBim) {
                setErrorMessage(
                    `A soma das atividades (${totalDistribuido.toString().replace('.', ',')}) ultrapassa a nota total do periodo (${totalGradeBim.toString().replace('.', ',')}).`
                )
                return
            }
        }

        setErrorMessage('')
        setSubmitting(true)

        try {
            if (editingId) {
                const res = await updateAvaliacao(
                    editingId, form.descricao.trim(), form.tipo, valorNum
                )

                const ok = res && (res.data?.activity || res.status === 200 || res.data?.success)
                if (ok) {
                    const matterDoc = matters.find(m => m._id === form.id_matter)
                    setActivities(prev => prev.map(a =>
                        a._id === editingId
                            ? {
                                ...a,
                                bimonthly: form.bimonthly,
                                descricao: form.descricao.trim(),
                                tipo: form.tipo,
                                valor: valorNum,
                                id_matter: matterDoc
                                    ? { _id: matterDoc._id, name: matterDoc.name }
                                    : a.id_matter
                            }
                            : a
                    ))
                    closeForm()
                } else {
                    setErrorMessage('Não foi possível atualizar a atividade.')
                }
            } else {
                const regentRaw = sessionStorage.getItem('classRegentTeacher')
                const regentId = extractId(safeParse(regentRaw))

                let id_teacher_payload, id_teacher02_payload

                if (isPhysicalTeacher) {
                    id_teacher_payload = idTeacher
                    id_teacher02_payload = null
                } else if (idTeacher02 && idTeacher === idTeacher02) {
                    id_teacher_payload = regentId || idTeacher
                    id_teacher02_payload = idTeacher
                } else {
                    id_teacher_payload = idTeacher
                    id_teacher02_payload = idTeacher02 || null
                }

                const res = await createActivity({
                    year,
                    bimonthly: form.bimonthly,
                    descricao: form.descricao.trim(),
                    tipo: form.tipo,
                    valor: valorNum,
                    id_teacher: id_teacher_payload,
                    id_teacher02: id_teacher02_payload,
                    id_matter: form.id_matter,
                    id_class: idClass,
                    [quarterIdKey]: bimDoc._id
                })

                if (res && res.data?.activity) {
                    sessionStorage.setItem('id-activity', res.data.activity._id)
                    const matterDoc = matters.find(m => m._id === form.id_matter)
                    const nova = {
                        ...res.data.activity,
                        id_matter: matterDoc
                            ? { _id: matterDoc._id, name: matterDoc.name }
                            : null
                    }
                    setActivities(prev => [nova, ...prev])

                    const mid = matterDoc?._id || 'sem_materia'
                    setOpenMatters(p => ({ ...p, [`${form.bimonthly}-${mid}`]: true }))
                    setOpenBimesters(p => ({ ...p, [form.bimonthly]: true }))

                    sessionStorage.setItem('Selectmatter', mid)
                    closeForm()
                    navigate('/$num-quarter-grade')
                } else {
                    setErrorMessage('Não foi possível criar a atividade.')
                }
            }
        } catch (err) {
            console.error(err)
            setErrorMessage('Erro ao salvar. Tente novamente.')
        } finally {
            setSubmitting(false)
        }
    }

    // ── delete ───────────────────────────────────────────────────────
    const cancelDelete = () => { if (!deleting) setPendingDelete(null) }

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete || deleting) return
        setDeleting(true)
        try {
            const res = await DestroyActivity(pendingDelete._id)
            if (res) {
                setActivities(prev => prev.filter(a => a._id !== pendingDelete._id))
                setPendingDelete(null)
            } else {
                alert('Não foi possível excluir a atividade.')
            }
        } catch (e) {
            console.error(e)
            alert('Erro ao excluir. Tente novamente.')
        } finally {
            setDeleting(false)
        }
    }, [pendingDelete, deleting])

    // ── render ───────────────────────────────────────────────────────
    // Skeleton enquanto carrega o essencial (atividades)
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <Title>Minhas Avaliações</Title>
                    </Header>
                    {[1, 2, 3].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="40%" $h="18px" />
                            <SkeletonLine $w="80%" />
                            <SkeletonLine $w="60%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <Title>Minhas Avaliações</Title>
                    <NewActivityButton
                        onClick={openCreate}
                        disabled={loadingSecondary}
                        title={loadingSecondary ? 'Carregando dados...' : 'Cadastrar nova avaliação'}
                    >
                        + Nova Avaliação
                    </NewActivityButton>
                </Header>

                <Toolbar>
                    <SearchInput
                        type="text"
                        placeholder={`Buscar por descrição, tipo, ${periodLabelLower} ou matéria...`}
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        aria-label="Buscar avaliações"
                    />
                    <ToolbarActions>
                        <Badge>
                            {totalAtividades} {totalAtividades === 1 ? 'avaliação' : 'avaliações'}
                        </Badge>
                        {grouped.length > 0 && (
                            <ToggleAllButton onClick={toggleAll}>
                                {allExpanded ? 'Colapsar tudo' : 'Expandir tudo'}
                            </ToggleAllButton>
                        )}
                    </ToolbarActions>
                </Toolbar>

                {grouped.length === 0 ? (
                    <EmptyState>
                        {debouncedBusca
                            ? `Nenhuma avaliação encontrada para "${debouncedBusca}".`
                            : 'Nenhuma atividade encontrada.'}
                    </EmptyState>
                ) : (
                    grouped.map((bim) => {
                        const bimOpen = !!openBimesters[bim.bimester]
                        const bimMeta = bimesterById[bim.bimester]
                        const totalNotaBimestre = toNumber(bimMeta?.totalGrade)
                        const mediaNotaBimestre = toNumber(bimMeta?.averageGrade)

                        return (
                            <BimesterCard key={bim.bimester}>
                                <BimesterHeader
                                    $open={bimOpen}
                                    onClick={() => toggleBimester(bim.bimester)}
                                    aria-expanded={bimOpen}
                                >
                                    <BimesterTitle>
                                        <span>
                                            Avaliações do {bim.bimester.replace('BIMESTRE', periodLabel.toUpperCase())}
                                        </span>
                                        <Badge>Total: {bim.total}</Badge>
                                    </BimesterTitle>
                                    <ActivityMeta>
                                        <Tag $variant="tipo">
                                            Nota Total: {bimMeta ? fmt(totalNotaBimestre) : '—'}
                                        </Tag>
                                        <Tag $variant="media">
                                            Nota Média: {bimMeta ? fmt(mediaNotaBimestre) : '—'}
                                        </Tag>
                                        <ChevronWrap $open={bimOpen}>▼</ChevronWrap>
                                    </ActivityMeta>
                                </BimesterHeader>

                                {bimOpen && (
                                    <BimesterBody>
                                        {bim.mattersList.map((m) => {
                                            const matterKey = `${bim.bimester}-${m.id}`
                                            const matterOpen = !!openMatters[matterKey]

                                            return (
                                                <MatterCard key={matterKey}>
                                                    <MatterHeader
                                                        $open={matterOpen}
                                                        onClick={() => toggleMatter(matterKey)}
                                                        aria-expanded={matterOpen}
                                                    >
                                                        <MatterTitle>
                                                            {m.name}
                                                            <Badge>{m.total}</Badge>
                                                        </MatterTitle>
                                                        <ActivityMeta>
                                                            <Tag $variant="valor">
                                                                Nota Distribuida: {fmt(m.soma)}
                                                            </Tag>
                                                            <ChevronWrap $open={matterOpen}>▼</ChevronWrap>
                                                        </ActivityMeta>
                                                    </MatterHeader>

                                                    {matterOpen && (
                                                        <MatterBody>
                                                            {m.activities.map((a) => {
                                                                const st = dailyStatus?.[a.bimonthly]
                                                                const isActivityOpen =
                                                                    (isPhysicalTeacher
                                                                        ? st?.physicalEducationTeacher
                                                                        : st?.regentTeacher) !== 'fechado'

                                                                return (
                                                                    <ActivityRowItem
                                                                        key={a._id}
                                                                        activity={a}
                                                                        isActivityOpen={isActivityOpen}
                                                                        studentTotal={contStudent.length}
                                                                        onViewGrades={goGrades}
                                                                        onEdit={openEdit}
                                                                        onDelete={askDelete}
                                                                    />
                                                                )
                                                            })}
                                                        </MatterBody>
                                                    )}
                                                </MatterCard>
                                            )
                                        })}
                                    </BimesterBody>
                                )}
                            </BimesterCard>
                        )
                    })
                )}

                <ToGoBack onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>

            {/* MODAL DE CRIAR / EDITAR */}
            {showForm && (
                <Overlay onClick={closeForm}>
                    <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <FormHeader>
                            <FormTitle>
                                {editingId ? 'Editar Avaliação' : 'Cadastro de Avaliação'}
                            </FormTitle>
                            <FormSubtitle>
                                {form.bimonthly?.replace('BIMESTRE', periodLabel.toUpperCase())}
                                {!isBimesterOpen && form.bimonthly && ' · FECHADO'}
                            </FormSubtitle>
                        </FormHeader>

                        {!editingId && currentBimesterMeta && (
                            <FormGroup>
                                <FormLabel>Resumo do {periodLabelLower}</FormLabel>
                                <ActivityMeta>
                                    <Tag $variant="valor">Total: {fmt(totalGradeBim)}</Tag>
                                    <Tag $variant="tipo">Média: {fmt(averageGradeBim)}</Tag>
                                    <Tag>Distribuído: {fmt(notaDistribuida)}</Tag>
                                    <Tag>
                                        Restante: {fmt(Math.max(0, totalGradeBim - notaDistribuida))}
                                    </Tag>
                                </ActivityMeta>
                            </FormGroup>
                        )}

                        {!editingId && (
                            <FormGroup>
                                <FormLabel>{periodLabel}</FormLabel>
                                <FormSelect
                                    name="bimonthly"
                                    value={form.bimonthly}
                                    onChange={handleChange}
                                >
                                    <option value="">Selecione</option>
                                    {availableBimesters.length === 0 ? (
                                        <option disabled value="">
                                            Sem {periodLabelLower} configurados
                                        </option>
                                    ) : (
                                        availableBimesters.map(b => (
                                            <option key={b} value={b}>
                                                {b.replace('BIMESTRE', periodLabel.toUpperCase())}
                                            </option>
                                        ))
                                    )}
                                </FormSelect>
                            </FormGroup>
                        )}

                        {!editingId && (
                            <FormGroup>
                                <FormLabel>Matéria</FormLabel>
                                <FormSelect
                                    name="id_matter"
                                    value={form.id_matter}
                                    onChange={handleChange}
                                >
                                    <option value="">Selecione</option>
                                    {matters.map(m => (
                                        <option key={m._id} value={m._id}>{m.name}</option>
                                    ))}
                                </FormSelect>
                            </FormGroup>
                        )}

                        <FormGroup>
                            <FormLabel>Descrição</FormLabel>
                            <FormInput
                                type="text"
                                name="descricao"
                                value={form.descricao}
                                onChange={handleChange}
                                placeholder="Ex.: Prova bimestral"
                                autoFocus
                            />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>Tipo</FormLabel>
                            <FormSelect name="tipo" value={form.tipo} onChange={handleChange}>
                                <option value="">selecione</option>
                                <option value="Normal">Normal</option>
                                <option value="Prova">Prova</option>
                                <option value="Trabalho">Trabalho</option>
                                <option value="Outra Atividade">Outra Atividade</option>
                            </FormSelect>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>Valor</FormLabel>
                            <FormInput
                                type="number"
                                step="0.1"
                                min="0"
                                inputMode="decimal"
                                name="valor"
                                value={form.valor}
                                onChange={handleChange}
                                placeholder="Ex.: 10"
                            />
                        </FormGroup>

                        {errorMessage && <FormError>{errorMessage}</FormError>}
                        {!isBimesterOpen && (
                            <FormError>
                                O diário deste periodo está fechado. Não é possível cadastrar nem editar atividades.
                            </FormError>
                        )}

                        <FormActions>
                            <CancelButton onClick={closeForm} disabled={submitting}>
                                Cancelar
                            </CancelButton>
                            <SaveButton
                                onClick={handleSubmit}
                                disabled={submitting || !isBimesterOpen}
                            >
                                {submitting
                                    ? (editingId ? 'Atualizando...' : 'Salvando...')
                                    : (editingId ? 'Atualizar' : 'Salvar')}
                            </SaveButton>
                        </FormActions>
                    </FormCard>
                </Overlay>
            )}

            {/* MODAL DE DELETE */}
            {pendingDelete && (
                <Overlay onClick={cancelDelete}>
                    <Modal onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
                        <ModalTitle>Apagar Avaliação?</ModalTitle>
                        <ModalText>
                            Tem certeza que deseja apagar <b>{pendingDelete.descricao}</b>?
                            Esta ação não pode ser desfeita.
                        </ModalText>
                        <ModalActions>
                            <Cancel onClick={cancelDelete} disabled={deleting}>Cancelar</Cancel>
                            <ConfirmDelete onClick={confirmDelete} disabled={deleting}>
                                {deleting ? 'Apagando...' : 'Apagar'}
                            </ConfirmDelete>
                        </ModalActions>
                    </Modal>
                </Overlay>
            )}
        </Container>
    )
}

export default Activities
